import * as React from 'react';

import {
  InventoryTransactionType,
  Unit,
} from '@rematter/frontend-lib-api-client';
import { Button, ConsoleDialog } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useSetAtom } from 'jotai';
import { LucideCheck } from 'lucide-react';

import {
  allocateToLotAtom,
  LotAllocationInput,
} from '../../../_graphql/mutationInventoryLot';
import { useTranslations } from '../../../i18n/useTranslations';

import { LotSelector } from './LotSelector';
import { MovementAllocationRow } from './MovementAllocationRow';

export type MovementForAllocationDialog = {
  id: string;
  itemName: string;
  occurredAt: string;
  quantity: number;
  unallocatedQuantity: number;
  quantityUnit: Unit;
  originTransactionType: InventoryTransactionType;
  originTransactionItemId: string;
};

type AllocationQuantity = {
  movementId: string;
  quantity: number;
};

type AllocateToLotDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  movements: MovementForAllocationDialog[];
  facilityId: string;
  onSuccess?: () => void;
};

export const AllocateToLotDialog = ({
  isOpen,
  onClose,
  movements,
  facilityId,
  onSuccess,
}: AllocateToLotDialogProps) => {
  const t = useTranslations();
  const toast = useToast();

  const [selectedLotId, setSelectedLotId] = React.useState<string | null>(null);
  const [allocations, setAllocations] = React.useState<AllocationQuantity[]>(
    [],
  );
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const allocateToLot = useSetAtom(allocateToLotAtom({}));

  React.useEffect(() => {
    if (isOpen && movements.length > 0) {
      setAllocations(
        movements.map((m) => ({
          movementId: m.id,
          quantity: m.unallocatedQuantity,
        })),
      );
    }
  }, [isOpen, movements]);

  const handleClose = React.useCallback(() => {
    setSelectedLotId(null);
    setAllocations([]);
    onClose();
  }, [onClose]);

  const handleQuantityChange = React.useCallback(
    (movementId: string, quantity: number) => {
      setAllocations((prev) =>
        prev.map((a) => (a.movementId === movementId ? { ...a, quantity } : a)),
      );
    },
    [],
  );

  const handleRemoveMovement = React.useCallback((movementId: string) => {
    setAllocations((prev) => prev.filter((a) => a.movementId !== movementId));
  }, []);

  const validAllocations = React.useMemo(
    () => allocations.filter((a) => a.quantity > 0),
    [allocations],
  );

  const canSubmit =
    selectedLotId && validAllocations.length > 0 && !isSubmitting;

  const handleSubmit = React.useCallback(async () => {
    if (!selectedLotId || validAllocations.length === 0) return;

    setIsSubmitting(true);

    try {
      const allocationInputs: LotAllocationInput[] = validAllocations.map(
        (alloc) => {
          const movement = movements.find((m) => m.id === alloc.movementId);
          return {
            movementId: alloc.movementId,
            quantity: {
              value: alloc.quantity,
              unit: movement?.quantityUnit || 'Pounds',
            },
          };
        },
      );

      await allocateToLot({
        lotId: selectedLotId,
        allocations: allocationInputs,
      });

      toast.success(t.lotsListView.allocation.success);
      onSuccess?.();
      handleClose();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : t.lotsListView.dialog.unknownError;
      toast.error(t.lotsListView.allocation.error + ': ' + errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    selectedLotId,
    validAllocations,
    movements,
    allocateToLot,
    toast,
    t,
    onSuccess,
    handleClose,
  ]);

  const movementsToShow = React.useMemo(
    () =>
      movements.filter((m) => allocations.some((a) => a.movementId === m.id)),
    [movements, allocations],
  );

  return (
    <ConsoleDialog
      className="@AllocateToLotDialog"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      title={t.lotsListView.allocation.allocateToLot}
      size="wrap"
      content={
        <div className="space-y-4">
          <LotSelector
            facilityId={facilityId}
            selectedLotId={selectedLotId}
            onSelect={setSelectedLotId}
            isRequired
          />

          {movementsToShow.length > 0 && (
            <div className="mt-4 space-y-2">
              <div className="text-sm font-medium text-neutral/75">
                {t.lotsListView.allocation.movementsToAllocate}
              </div>
              <div className="max-h-[300px] space-y-2 overflow-y-auto">
                {movementsToShow.map((movement) => {
                  const alloc = allocations.find(
                    (a) => a.movementId === movement.id,
                  );
                  return (
                    <MovementAllocationRow
                      key={movement.id}
                      originTransactionType={movement.originTransactionType}
                      originTransactionItemId={movement.originTransactionItemId}
                      itemName={movement.itemName}
                      occurredAt={movement.occurredAt}
                      quantity={alloc?.quantity ?? 0}
                      maxQuantity={movement.unallocatedQuantity}
                      quantityUnit={movement.quantityUnit}
                      onChange={(qty) => handleQuantityChange(movement.id, qty)}
                      onRemove={
                        movements.length > 1
                          ? () => handleRemoveMovement(movement.id)
                          : undefined
                      }
                      isDisabled={isSubmitting}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {movementsToShow.length === 0 && (
            <div className="py-4 text-center text-sm text-neutral/60">
              {t.lotsListView.allocation.noMovementsSelected}
            </div>
          )}
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            color="neutral"
            onPress={handleClose}
            isDisabled={isSubmitting}
          >
            {t.lotsListView.dialog.cancel}
          </Button>
          <Button
            icon={<LucideCheck />}
            color="primary"
            onPress={handleSubmit}
            isDisabled={!canSubmit}
            isLoading={isSubmitting}
          >
            {t.lotsListView.allocation.allocate}
          </Button>
        </div>
      }
    />
  );
};
